import { Add } from '@mui/icons-material';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { createDocumentsConfirming } from 'lib/api/confirming';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import {
  formatDateBack,
  formatMoney,
  formatMoneyBack,
  showAlert,
  validaRut,
} from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

interface AddDocumentsConfirmingProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  stakeholderIdentifier: string;
  handleSearchSuggestedDocuments: () => void;
}

export interface NewDocument {
  folio: string;
  businessIdentifier: string;
  businessName: string;
  stakeholderIdentifier: string;
  issuedDate: string;
  amount: number;
  subType: string;
}

const AddDocumentsConfirming = (props: AddDocumentsConfirmingProps) => {
  const {
    open,
    setOpen,
    stakeholderIdentifier,
    handleSearchSuggestedDocuments,
  } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<NewDocument[]>([]);
  const [newDocument, setNewDocument] = useState<NewDocument>({
    folio: '',
    businessIdentifier: '',
    businessName: '',
    stakeholderIdentifier: stakeholderIdentifier,
    issuedDate: '',
    amount: 0,
    subType: '33',
  });

  const validateRuts = () => {
    if (validaRut(newDocument.businessIdentifier)) {
      return true;
    }
    return false;
  };

  const checkAllFields = () => {
    if (
      newDocument.folio === '' ||
      newDocument.businessIdentifier === '' ||
      newDocument.businessName === '' ||
      newDocument.stakeholderIdentifier === '' ||
      newDocument.amount === 0 ||
      newDocument.subType === ''
    ) {
      return false;
    }
    return true;
  };

  const handleChangeDate = (date: string) => {
    const [year, month, day] = date.split('-');
    setNewDocument({ ...newDocument, issuedDate: `${day}${month}${year}` });
  };

  const uploadDocuments = async () => {
    setLoading(true);
    try {
      const formatDocumentsWithDate = documents.map((invoice) => {
        return {
          ...invoice,
          issuedDate: new Date(invoice.issuedDate).toISOString(),
        };
      });
      await createDocumentsConfirming(dispatch, {
        documents: formatDocumentsWithDate,
        stakeholderIdentifier: stakeholderIdentifier,
      });
      void handleSearchSuggestedDocuments();
      setOpen(false);
      showAlert(dispatch, 'success', 'Documentos agregados correctamente');
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al agregar documentos');
    }
    setLoading(false);
  };

  const handleAddInvoice = async () => {
    setLoading(true);
    if (!checkAllFields()) {
      showAlert(dispatch, 'error', 'Debes llenar todos los campos');
      return setLoading(false);
    }
    if (!validateRuts()) {
      showAlert(dispatch, 'error', 'RUT inválido');
      return setLoading(false);
    }
    newDocument.issuedDate = formatDateBack(newDocument.issuedDate);
    setDocuments((prev: NewDocument[]) => [...prev, newDocument]);
    setNewDocument({
      folio: '',
      businessIdentifier: '',
      businessName: '',
      stakeholderIdentifier: stakeholderIdentifier,
      issuedDate: '',
      amount: 0,
      subType: '33',
    });
    setLoading(false);
  };

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog maxWidth="lg" open={open}>
      <DialogTitle>
        <HeaderWithActions title="Agregar documentos" type="dialogHeader" />
      </DialogTitle>
      {loading ? (
        <CenteredCircularProgress type="dialog" />
      ) : (
        <DialogContent>
          <Typography variant="body1">
            A continuación, ingresa los datos de los documentos que quieres
            ingresar.
          </Typography>
          <div style={{ padding: '20px 0px' }}>
            <Grid container spacing={2} alignItems={'center'}>
              <Grid item xs={8 / 5}>
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-label">Tipo</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label="Tipo"
                    value={newDocument.subType}
                    onChange={(e) =>
                      setNewDocument({
                        ...newDocument,
                        subType: String(e.target.value),
                      })
                    }
                  >
                    <MenuItem value="33">Factura electrónica</MenuItem>
                    <MenuItem value="34">Factura exenta</MenuItem>
                    <MenuItem value="46">Factura de compra</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={8 / 5}>
                <TextField
                  fullWidth
                  label="Folio"
                  value={newDocument.folio}
                  onChange={(e) =>
                    setNewDocument({ ...newDocument, folio: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={8 / 5}>
                <TextField
                  fullWidth
                  label="RUT proveedor *"
                  value={newDocument.businessIdentifier}
                  onChange={(e) =>
                    setNewDocument({
                      ...newDocument,
                      businessIdentifier: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={8 / 5}>
                <TextField
                  fullWidth
                  label="Nombre proveedor"
                  value={newDocument.businessName}
                  onChange={(e) =>
                    setNewDocument({
                      ...newDocument,
                      businessName: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Fecha de Emisión"
                  type="date"
                  value={formatDateBack(newDocument.issuedDate)}
                  onChange={(e) => handleChangeDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={8 / 5}>
                <TextField
                  fullWidth
                  label="Monto"
                  value={formatMoney(newDocument.amount)}
                  onChange={(e) =>
                    setNewDocument({
                      ...newDocument,
                      amount: formatMoneyBack(e.target.value),
                    })
                  }
                />
              </Grid>

              <Grid item xs={1}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <IconButton
                    color="primary"
                    onClick={() => {
                      void handleAddInvoice();
                    }}
                  >
                    <Add />
                  </IconButton>
                )}
              </Grid>
            </Grid>
            <Typography variant="caption">
              * Rut sin puntos y con guión
            </Typography>
          </div>
          {documents.length > 0 && (
            <div>
              <Typography variant="body1">Documentos:</Typography>
              <div>
                <EnhancedTable
                  dataHeaders={[
                    { key: 'subType', label: 'Tipo', type: 'invoice-sub-type' },
                    { key: 'folio', label: 'Folio' },
                    { key: 'businessIdentifier', label: 'Rut proveedor' },
                    { key: 'businessName', label: 'Nombre proveedor' },
                    {
                      key: 'issuedDate',
                      label: 'Fecha emisión',
                      type: 'eight-days-date',
                    },
                    { key: 'amount', label: 'Monto', type: 'money' },
                  ]}
                  data={documents}
                  finalSumRow
                />
              </div>
            </div>
          )}
        </DialogContent>
      )}
      <DialogActions>
        <CustomButton
          variant="contained"
          color="secondary"
          onClick={() => setOpen(false)}
        >
          Salir
        </CustomButton>
        <CustomButton
          variant="contained"
          color="primary"
          disabled={!documents.length}
          onClick={() => {
            void uploadDocuments();
          }}
        >
          Subir
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddDocumentsConfirming;
